// Demo LP profile + portfolio for the investor portal.
// Numbers are illustrative.
//
// FIVE INVESTOR ROUTES (Daksfirst product structure):
//   1. uk_warehouse_fl    — UK Bridging Warehouse, first-loss tranche
//   2. uk_whole_loan_bnote — UK Whole-Loan B-Note (deal-by-deal, behind wholesale lines)
//   3. hospitality        — Hospitality credit (deal-by-deal, senior/mezz on hotel real estate)
//   4. india_lux_feeder   — India via Luxembourg Securitisation compartment
//   5. india_ncd_direct   — India Non-Convertible Debentures, direct subscription

export const DEMO_LP = {
  name: "Demo Family Office Limited",
  contact: "Demo Investor",
  email: "demo@daksfirst.com",
  primaryRelationship: "info@daksfirst.com",
  jurisdiction: "Cayman Islands",
  kycStatus: "Verified",
  kycDate: "2026-01-14",
  entities: [
    { id: "ent-1", name: "Demo Family Office Limited", primary: true },
    { id: "ent-2", name: "Demo Trust 2024" }
  ]
};

// Currency formatter
export function fmt(n, ccy = "USD") {
  if (n == null || isNaN(n)) return "—";
  const sign = n < 0 ? "(" : "";
  const close = n < 0 ? ")" : "";
  const abs = Math.abs(n);
  const parts = abs.toFixed(2).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${sign}${ccy} ${parts.join(".")}${close}`;
}

export function fmtPct(n) {
  if (n == null || isNaN(n)) return "—";
  return `${n.toFixed(1)}%`;
}

// Friendly type labels for the 5 product routes
export function commitmentTypeLabel(type) {
  switch (type) {
    case "uk_warehouse_fl":     return "UK Warehouse · Pool B-Note";
    case "uk_whole_loan_bnote": return "UK Whole-Loan · Single-deal B-Note";
    case "hospitality":         return "Hospitality";
    case "india_lux_feeder":    return "India · Lux Feeder";
    case "india_ncd_direct":    return "India · NCD direct";
    // legacy values from earlier demo data (kept for backward compat)
    case "warehouse":           return "Pooled fund";
    case "whole_loan_facility": return "Whole-loan facility";
    default:                    return type || "—";
  }
}

// Each commitment maps to one of the 5 product routes
export const COMMITMENTS = [
  {
    slug: "uk-warehouse-fl-2024",
    fund: "Daksfirst UK Bridging Warehouse — First-Loss Tranche",
    type: "uk_warehouse_fl",
    capitalStack: "B-Note position on the warehouse pool (subordinated debt below the senior wholesale A-Note)",
    vehicle: "UK SPV — Daksfirst Warehouse FL Co.",
    domicile: "United Kingdom",
    fundAdmin: "In-house ledger + Maystone Capital (FCA AR)",
    auditor: "To be appointed (pre-launch)",
    banker: "HSBC",
    vintage: 2024,
    currency: "GBP",
    committed:       1500000,
    called:          1500000,
    contributedPaid: 1500000,
    contributedGAAP: 1500000,
    distributed:      75000,
    nav:            1612500,
    grossIRR: 13.4,
    netIRR:   12.1,
    moic:     1.12,
    dpi:      0.05,
    tvpi:     1.12,
    sector:        "UK bridging — pool B-Note across the warehouse book",
    strategy:      "Subordinated B-Note position on the entire UK bridging warehouse pool. The senior wholesale facility (A-Note) is advanced against the borrowing base; this tranche sits below it, absorbing losses before the senior. Same structural position as a single-deal B-Note — but diversified across the whole book rather than concentrated on one loan. Pool-level diversification, higher coupon than the A-Note.",
    targetNetReturn:"12–15% net IRR",
    fees:          "1.5% mgmt + 20% performance over 8% hurdle",
    primaryContact:"info@daksfirst.com"
  },
  {
    slug: "uk-whole-loan-bnote",
    fund: "Daksfirst UK Whole-Loan Facility — B-Note participation",
    type: "uk_whole_loan_bnote",
    capitalStack: "B-Note (mezzanine to wholesale, senior to first-loss)",
    vehicle: "Per-deal SPV — B-Note participation note",
    domicile: "United Kingdom",
    fundAdmin: "Daksfirst (in-house ledger) + Maystone Capital (FCA AR)",
    auditor: "Per-deal SPV auditors",
    vintage: "Rolling",
    currency: "GBP",
    committed:       3000000,
    called:          1850000,
    contributedPaid: 1850000,
    contributedGAAP: 1850000,
    distributed:      220000,
    nav:            1675000,
    grossIRR: 9.8,
    netIRR:   9.1,
    moic:     1.05,
    dpi:      0.12,
    tvpi:     1.04,
    sector:        "UK bridging — investment-by-investment",
    strategy:      "B-Note position behind Daksfirst's wholesale lines on each loan. LP elects each deal individually after pre-IC memo. Coupon income passes through monthly.",
    targetNetReturn:"9–11% net IRR per deal",
    fees:          "0.5% arrangement per deal + 1% servicing on outstanding. No carry.",
    primaryContact:"info@daksfirst.com"
  },
  {
    slug: "hospitality-2025",
    fund: "Daksfirst Hospitality Credit",
    type: "hospitality",
    capitalStack: "Senior or mezzanine secured against operating real estate",
    vehicle: "Per-deal SPV (Project-named, ringfenced)",
    domicile: "United Kingdom / Luxembourg (deal-dependent)",
    fundAdmin: "Per-deal SPV administration",
    auditor: "Per-deal SPV auditors",
    vintage: "Rolling",
    currency: "EUR",
    committed:       2500000,
    called:           750000,
    contributedPaid:  750000,
    contributedGAAP:  750000,
    distributed:           0,
    nav:              780000,
    grossIRR: 11.5,
    netIRR:   10.4,
    moic:     1.04,
    dpi:      0.0,
    tvpi:     1.04,
    sector:        "Hospitality — hotels, branded residences, mezzanine + senior",
    strategy:      "Senior or mezzanine credit into hospitality acquisitions, refinancings and pre-opening capex. Secured against operating real estate. Deal sizes typically £15-100m total facility, LP tickets £250k-£5m.",
    targetNetReturn:"10–13% net IRR",
    fees:          "1% arrangement + 1.25% servicing on outstanding",
    primaryContact:"info@daksfirst.com"
  },
  {
    slug: "india-lux-c2",
    fund: "Daksfirst Lux Securitisation — India Compartment 2",
    type: "india_lux_feeder",
    capitalStack: "Feeder into Indian credit (NCDs + AIF SS)",
    vehicle: "Luxembourg Securitisation Vehicle — segregated compartment",
    domicile: "Luxembourg",
    fundAdmin: "Maples FS",
    auditor: "PwC",
    banker: "Citibank",
    legal: "Maples and Calder",
    structuring: "EY",
    vintage: 2025,
    currency: "EUR",
    committed:       2500000,
    called:          1875000,
    contributedPaid: 1875000,
    contributedGAAP: 1875000,
    distributed:           0,
    nav:            1985625,
    grossIRR: 9.1,
    netIRR:   7.3,
    moic:     1.06,
    dpi:      0.0,
    tvpi:     1.06,
    sector:        "India private credit (Lux feeder)",
    strategy:      "European wrapper feeding into Daksfirst India Special Situations Fund I and selected NCD positions. Per-investor segregated compartment for tax/AML isolation.",
    targetNetReturn:"12–14% net IRR (INR-equivalent), 11-13% USD-hedged",
    fees:          "1.5% mgmt / 15% carry over 8% hurdle (passed through)",
    primaryContact:"info@daksfirst.com"
  },
  {
    slug: "india-ncd-direct",
    fund: "Daksfirst India NCDs — Direct subscriptions",
    type: "india_ncd_direct",
    capitalStack: "Direct holding (LP is the bondholder of record)",
    vehicle: "SEBI-regulated NCDs (listed or private placement)",
    domicile: "India",
    fundAdmin: "KFin Technologies (registrar) per issuance",
    auditor: "Issuer-side audit",
    vintage: "Rolling",
    currency: "INR",
    committed:      150000000,
    called:          50000000,
    contributedPaid: 50000000,
    contributedGAAP: 50000000,
    distributed:      2500000,
    nav:             52400000,
    grossIRR: 12.8,
    netIRR:   11.6,
    moic:     1.05,
    dpi:      0.05,
    tvpi:     1.04,
    sector:        "Indian NCDs — listed and private placement",
    strategy:      "Direct subscription to NCDs originated by Daksfirst (KIFPL via IFHL service provider) and curated third-party Indian issuers. Suited to India tax-resident LPs or sophisticated cross-border investors comfortable with TDS withholding.",
    targetNetReturn:"11–14% net (INR)",
    fees:          "0.25% subscription per deal. No carry.",
    primaryContact:"info@daksfirst.com"
  }
];

// Capital account ledger — applies primarily to pooled / per-deal commitments.
// For warehouse first-loss and whole-loan B-Note we surface a simplified roll-forward.
export const CAPITAL_ACCOUNTS = {
  "uk-warehouse-fl-2024": {
    period: "01/01/2026 — 03/31/2026",
    summary: [
      { label: "Beginning balance",                    period: 1572000, itd: 0 },
      { label: "Capital contributions",                period: 0,       itd: 1500000 },
      { label: "Capital distributions",                period: -25000,  itd: -75000 },
      { label: "Senior interest serviced (allocated)", period: -42000,  itd: -168000 },
      { label: "Net interest received (book level)",   period: 78000,   itd: 312500 },
      { label: "Provisioning / impairments",           period: -5500,   itd: -22000 },
      { label: "Management fees",                      period: -5625,   itd: -22500 },
      { label: "Performance fees accrued",             period: -3500,   itd: -12000 }
    ],
    endingBalance: { period: 1612500, itd: 1612500 },
    commitment: [
      { label: "Initial commitment", period: 1500000, itd: 1500000 },
      { label: "Drawn down",         period: 0,       itd: 1500000 }
    ],
    unfunded: { period: 0, itd: 0 }
  },
  "uk-whole-loan-bnote": {
    period: "01/01/2026 — 03/31/2026",
    summary: [
      { label: "Beginning balance",        period: 1612500, itd: 0 },
      { label: "Capital contributions",    period: 350000,  itd: 1850000 },
      { label: "Capital distributions",    period: -45000,  itd: -220000 },
      { label: "Coupon income",            period: 38500,   itd: 162500 },
      { label: "Servicing fees",           period: -4625,   itd: -18900 },
      { label: "Origination / arrangement",period: -1750,   itd: -9250 },
      { label: "Net realized gain (loss)", period: 0,       itd: 0 },
      { label: "Net unrealized mark",      period: -274625, itd: -89350 }
    ],
    endingBalance: { period: 1675000, itd: 1675000 },
    commitment: [
      { label: "Whitelist commitment", period: 3000000, itd: 3000000 },
      { label: "Cumulative deployed",  period: 350000,  itd: 1850000 }
    ],
    unfunded: { period: 1325000, itd: 1325000 }
  },
  "hospitality-2025": {
    period: "01/01/2026 — 03/31/2026",
    summary: [
      { label: "Beginning balance",        period: 760000, itd: 0 },
      { label: "Capital contributions",    period: 0,      itd: 750000 },
      { label: "Capital distributions",    period: 0,      itd: 0 },
      { label: "Coupon income",            period: 21500,  itd: 42500 },
      { label: "Servicing fees",           period: -1500,  itd: -3000 },
      { label: "Net unrealized mark",      period: 0,      itd: -9500 }
    ],
    endingBalance: { period: 780000, itd: 780000 },
    commitment: [
      { label: "Commitment to programme", period: 2500000, itd: 2500000 },
      { label: "Cumulative deployed",     period: 0,       itd: 750000 }
    ],
    unfunded: { period: 1750000, itd: 1750000 }
  },
  "india-lux-c2": {
    period: "01/01/2026 — 03/31/2026",
    summary: [
      { label: "Beginning balance",        period: 1928125, itd: 0 },
      { label: "Capital contributions",    period: 0,       itd: 1875000 },
      { label: "Capital distributions",    period: 0,       itd: 0 },
      { label: "Compartment expenses",     period: 0,       itd: -7500 },
      { label: "Net unrealized mark",      period: 42188,   itd: 142188 },
      { label: "Management fees",          period: -7031,   itd: -21094 },
      { label: "Carried interest accrued", period: 0,       itd: -36063 },
      { label: "Net operating income",     period: 22343,   itd: 33094 }
    ],
    endingBalance: { period: 1985625, itd: 1985625 },
    commitment: [
      { label: "Compartment commitment", period: 2500000, itd: 2500000 },
      { label: "Drawn down",             period: 0,       itd: 1875000 }
    ],
    unfunded: { period: 625000, itd: 625000 }
  },
  "india-ncd-direct": {
    period: "01/01/2026 — 03/31/2026",
    summary: [
      { label: "Beginning balance",                period: 50800000, itd: 0 },
      { label: "NCD subscriptions",                period: 0,        itd: 50000000 },
      { label: "Coupon received (gross)",          period: 1750000,  itd: 5500000 },
      { label: "Indian withholding tax (TDS 10%)", period: -175000,  itd: -550000 },
      { label: "Coupon distributions to LP",       period: -1575000, itd: -2500000 },
      { label: "Mark-to-model on outstanding",     period: 1600000,  itd: -50000 },
      { label: "Subscription fees",                period: 0,        itd: -125000 }
    ],
    endingBalance: { period: 52400000, itd: 52400000 },
    commitment: [
      { label: "NCD whitelist commitment", period: 150000000, itd: 150000000 },
      { label: "Subscribed",               period: 0,         itd: 50000000 }
    ],
    unfunded: { period: 100000000, itd: 100000000 }
  }
};

// Fund-level transaction ledger
export const FUND_TRANSACTIONS = {
  "uk-warehouse-fl-2024": [
    { date: "2026-03-31", type: "Performance fee accrued", amount: -3500,  notes: "Q1 accrual" },
    { date: "2026-03-31", type: "Net interest received",   amount: 78000,  notes: "Q1 — book level allocation" },
    { date: "2026-03-31", type: "Provisioning",            amount: -5500,  notes: "Watch-list mark" },
    { date: "2026-03-31", type: "Management fees",         amount: -5625,  notes: "Q1 fee" },
    { date: "2026-02-28", type: "Capital distribution",    amount: -25000, notes: "Quarterly distribution" },
    { date: "2025-12-31", type: "Net interest received",   amount: 95000,  notes: "Q4" },
    { date: "2024-09-15", type: "Capital contribution",    amount: 1500000,notes: "Initial drawdown" }
  ],
  "uk-whole-loan-bnote": [
    { date: "2026-03-31", type: "Coupon income",        amount: 38500,   notes: "Q1 across active deals" },
    { date: "2026-03-31", type: "Servicing fees",       amount: -4625,   notes: "Q1 servicing 1% on outstanding" },
    { date: "2026-02-15", type: "Capital distribution", amount: -45000,  notes: "Manchester retail park — early repayment principal" },
    { date: "2026-01-22", type: "Capital contribution", amount: 350000,  notes: "Funded — Birmingham development" },
    { date: "2026-01-22", type: "Origination",          amount: -1750,   notes: "0.5% arrangement fee" },
    { date: "2025-12-31", type: "Coupon income",        amount: 41000,   notes: "Q4 interest" },
    { date: "2025-11-08", type: "Capital contribution", amount: 600000,  notes: "Funded — London Mayfair refurb" },
    { date: "2025-09-12", type: "Capital contribution", amount: 500000,  notes: "Funded — Manchester retail park" }
  ],
  "hospitality-2025": [
    { date: "2026-03-31", type: "Coupon income",        amount: 21500,  notes: "Q1 — Project Arthur Sheraton (mezzanine)" },
    { date: "2026-03-31", type: "Servicing fees",       amount: -1500,  notes: "Q1 servicing" },
    { date: "2025-11-20", type: "Capital contribution", amount: 750000, notes: "Funded — Project Arthur Sheraton mezz position" }
  ],
  "india-lux-c2": [
    { date: "2026-03-31", type: "Net unrealized mark",      amount: 42188,  notes: "Underlying India SSF / NCD mark" },
    { date: "2026-03-31", type: "Management fees",          amount: -7031,  notes: "Q1 fee" },
    { date: "2025-12-31", type: "Carried interest accrued", amount: -36063, notes: "Annual accrual" },
    { date: "2025-12-31", type: "Net unrealized mark",      amount: 100000, notes: "Mark" },
    { date: "2025-06-30", type: "Capital contribution",     amount: 1875000,notes: "Compartment drawdown #1" }
  ],
  "india-ncd-direct": [
    { date: "2026-03-31", type: "Coupon received",     amount: 1750000,  notes: "Q1 across NCD positions" },
    { date: "2026-03-31", type: "TDS withholding",     amount: -175000,  notes: "Indian source 10%" },
    { date: "2026-03-31", type: "Net coupon to LP",    amount: -1575000, notes: "Distributed to LP wire" },
    { date: "2026-03-31", type: "Mark-to-model",       amount: 1600000,  notes: "MTM gain on rate move" },
    { date: "2025-08-22", type: "NCD subscription",    amount: 50000000, notes: "KIFPL Series A NCD subscription" }
  ]
};

// Investment-by-investment disclosure.
// Per-deal positions for whole-loan B-Note + hospitality + NCD direct;
// look-through (pooled) for UK warehouse FL + India Lux feeder.
export const INVESTMENTS = {
  "uk-warehouse-fl-2024": [
    // Pool look-through — top 5 active positions in the warehouse
    { borrower: "London Mayfair refurb",     sector: "Bridging — refurb",      jurisdiction: "UK", ltv: "58%", dscr: "n/a", rate: "SONIA+725", maturity: "2026-11", status: "Performing" },
    { borrower: "Manchester retail park",    sector: "Bridging — refi",        jurisdiction: "UK", ltv: "55%", dscr: "n/a", rate: "SONIA+650", maturity: "2026-09", status: "Performing — partial repaid" },
    { borrower: "Edinburgh townhouse",       sector: "Bridging — auction",     jurisdiction: "UK", ltv: "62%", dscr: "n/a", rate: "SONIA+775", maturity: "2026-07", status: "Performing" },
    { borrower: "Birmingham development",    sector: "Bridging — dev exit",    jurisdiction: "UK", ltv: "60%", dscr: "n/a", rate: "SONIA+700", maturity: "2026-12", status: "Performing" },
    { borrower: "Glasgow West End refurb",   sector: "Bridging — refurb",      jurisdiction: "UK", ltv: "57%", dscr: "n/a", rate: "SONIA+700", maturity: "2027-01", status: "Performing" }
  ],
  "uk-whole-loan-bnote": [
    { dealId: "WL-2025-04",
      borrower: "London Mayfair refurb",
      sector: "Bridging — refurb",
      jurisdiction: "UK",
      lpCommitment: 600000, lpDrawn: 600000, principalOutstanding: 600000,
      ltv: "58%", dscr: "n/a — bridging",
      rate: "SONIA+725",
      couponEarnedYTD: 17400,
      origination: "2025-11-08", maturity: "2026-11", status: "Performing"
    },
    { dealId: "WL-2025-03",
      borrower: "Manchester retail park",
      sector: "Bridging — refi",
      jurisdiction: "UK",
      lpCommitment: 500000, lpDrawn: 500000, principalOutstanding: 455000,
      ltv: "55%", dscr: "n/a — bridging",
      rate: "SONIA+650",
      couponEarnedYTD: 23100,
      origination: "2025-09-12", maturity: "2026-09", status: "Performing — partial repayment Feb 2026"
    },
    { dealId: "WL-2025-02",
      borrower: "Edinburgh townhouse",
      sector: "Bridging — auction",
      jurisdiction: "UK",
      lpCommitment: 400000, lpDrawn: 400000, principalOutstanding: 400000,
      ltv: "62%", dscr: "n/a — bridging",
      rate: "SONIA+775",
      couponEarnedYTD: 28000,
      origination: "2025-07-04", maturity: "2026-07", status: "Performing"
    },
    { dealId: "WL-2026-01",
      borrower: "Birmingham development",
      sector: "Bridging — dev exit",
      jurisdiction: "UK",
      lpCommitment: 350000, lpDrawn: 350000, principalOutstanding: 220000,
      ltv: "60%", dscr: "n/a — bridging",
      rate: "SONIA+700",
      couponEarnedYTD: 5250,
      origination: "2026-01-22", maturity: "2026-12", status: "Performing"
    }
  ],
  "hospitality-2025": [
    { dealId: "HOSP-2025-01",
      borrower: "Project Arthur Sheraton",
      sector: "Hospitality — Sheraton mezzanine",
      jurisdiction: "United Kingdom",
      lpCommitment: 750000, lpDrawn: 750000, principalOutstanding: 750000,
      ltv: "68% combined senior+mezz",
      dscr: "1.34x at base case",
      rate: "SONIA+925",
      couponEarnedYTD: 21500,
      origination: "2025-11-20", maturity: "2028-11", status: "Performing"
    }
  ],
  "india-lux-c2": [
    { borrower: "Pass-through to India SSF I", sector: "Special situations", jurisdiction: "India (via Lux)", ltv: "—",  dscr: "—",     rate: "—",          maturity: "—",       status: "Pass-through" },
    { borrower: "Pass-through to NCD basket",  sector: "NCD portfolio",      jurisdiction: "India (via Lux)", ltv: "—",  dscr: "—",     rate: "—",          maturity: "—",       status: "Pass-through" }
  ],
  "india-ncd-direct": [
    { dealId: "NCD-2025-01",
      borrower: "KIFPL Series A NCDs",
      sector: "NBFC senior secured",
      jurisdiction: "India",
      lpCommitment: 50000000, lpDrawn: 50000000, principalOutstanding: 50000000,
      ltv: "—",
      dscr: "1.45x (KIFPL aggregate)",
      rate: "13.5% pa coupon, semi-annual",
      couponEarnedYTD: 1750000,
      origination: "2025-08-22", maturity: "2028-08", status: "Performing — listed on BSE"
    }
  ]
};

export const WIRE_INSTRUCTIONS = {
  bankName: "—",
  bankAddress: "—",
  swift: "—",
  iban: "—",
  beneficiary: "Demo Family Office Limited",
  status: "not-set"
};

// Cross-fund document vault
export const DOCUMENTS = [
  { id: "d-001", title: "Q1 2026 NAV Statement — UK Warehouse FL Tranche", fund: "uk-warehouse-fl-2024",   type: "Quarterly Statement", deliveredBy: "Daksfirst Fund Admin", date: "2026-04-15", status: "Unread" },
  { id: "d-002", title: "Q1 2026 Roll-Forward — UK Whole-Loan B-Note",     fund: "uk-whole-loan-bnote",    type: "Quarterly Statement", deliveredBy: "Daksfirst",            date: "2026-04-10", status: "Unread" },
  { id: "d-003", title: "Q1 2026 Statement — Hospitality Programme",        fund: "hospitality-2025",       type: "Quarterly Statement", deliveredBy: "Daksfirst",           date: "2026-04-18", status: "Unread" },
  { id: "d-004", title: "Q1 2026 Compartment Statement — India Lux C2",     fund: "india-lux-c2",           type: "Quarterly Statement", deliveredBy: "Maples FS",              date: "2026-04-22", status: "Unread" },
  { id: "d-005", title: "Q1 2026 NCD Coupon Statement — India NCDs",        fund: "india-ncd-direct",       type: "Quarterly Statement", deliveredBy: "KFin Technologies",    date: "2026-04-12", status: "Unread" },
  { id: "d-006", title: "Capital Call Notice #2 — UK Warehouse FL",         fund: "uk-warehouse-fl-2024",   type: "Capital Call",        deliveredBy: "Daksfirst Fund Admin", date: "2025-08-30", status: "Read" },
  { id: "d-007", title: "Audited Financials 2025 — UK Warehouse FL Co.",    fund: "uk-warehouse-fl-2024",   type: "Audit Report",        deliveredBy: "External auditor",              date: "2026-03-12", status: "Read" },
  { id: "d-008", title: "Subscription Agreement — UK Warehouse FL",         fund: "uk-warehouse-fl-2024",   type: "Legal",               deliveredBy: "Daksfirst",            date: "2024-09-14", status: "Read" },
  { id: "d-009", title: "Whitelist Agreement — UK Whole-Loan B-Note",       fund: "uk-whole-loan-bnote",    type: "Legal",               deliveredBy: "Daksfirst",            date: "2025-06-18", status: "Read" },
  { id: "d-010", title: "Deal Memo — WL-2026-01 Birmingham",                fund: "uk-whole-loan-bnote",    type: "Deal Memo",           deliveredBy: "Daksfirst",            date: "2026-01-15", status: "Read" },
  { id: "d-011", title: "Mezzanine Credit Agreement — Project Arthur",      fund: "hospitality-2025",       type: "Legal",               deliveredBy: "Daksfirst",            date: "2025-11-15", status: "Read" },
  { id: "d-012", title: "Compartment Subscription — India Lux C2",          fund: "india-lux-c2",           type: "Legal",               deliveredBy: "Daksfirst",            date: "2025-05-22", status: "Read" },
  { id: "d-013", title: "Lux Compartment 2 — Annual Tax Voucher 2025",      fund: "india-lux-c2",           type: "Tax Document",        deliveredBy: "Maples FS",              date: "2026-03-30", status: "Read" },
  { id: "d-014", title: "NCD Information Memorandum — KIFPL Series A",      fund: "india-ncd-direct",       type: "Offering Document",   deliveredBy: "KIFPL",                date: "2025-08-10", status: "Read" },
  { id: "d-015", title: "Form 16A (TDS) — KIFPL NCD Q4 FY26",               fund: "india-ncd-direct",       type: "Tax Document",        deliveredBy: "KIFPL",                date: "2026-04-12", status: "Unread" },
  { id: "d-016", title: "Annual Investor Letter — Daksfirst Group 2025",    fund: null,                     type: "Annual Letter",       deliveredBy: "Daksfirst",            date: "2026-02-10", status: "Read" }
];

export const DOC_CATEGORIES = [
  "All Documents",
  "Quarterly Statement",
  "Capital Call",
  "Distribution Notice",
  "Annual Letter",
  "Audit Report",
  "Tax Document",
  "Offering Document",
  "Deal Memo",
  "Legal",
  "General"
];

// Group commitments by route for the dashboard. Returns a Map of {route → array of commitments}.
export function groupCommitmentsByRoute(commitments = COMMITMENTS) {
  const groups = new Map();
  for (const c of commitments) {
    if (!groups.has(c.type)) groups.set(c.type, []);
    groups.get(c.type).push(c);
  }
  return groups;
}

// Friendly description shown alongside each route in the dashboard
export function routeBlurb(type) {
  switch (type) {
    case "uk_warehouse_fl":
      return "B-Note position on the UK bridging warehouse pool. Subordinated to the senior wholesale facility, diversified across the whole book of underlying loans. Same structural position as a single-deal B-Note, just pooled.";
    case "uk_whole_loan_bnote":
      return "Single-deal B-Note participation. You elect each loan after pre-IC memo. Subordinated to the senior wholesale facility on that loan; concentrated exposure to one borrower.";
    case "hospitality":
      return "Senior or mezzanine credit secured against operating real estate in hotels and branded residences. Per-deal SPV, ringfenced.";
    case "india_lux_feeder":
      return "Luxembourg SCSp compartment feeding into Daksfirst India Special Situations Fund I and curated NCDs. EU-friendly tax wrapper.";
    case "india_ncd_direct":
      return "Direct subscription to listed and private placement NCDs originated by Daksfirst (via KIFPL/IFHL). Suited to India tax-resident or sophisticated cross-border LPs.";
    default:
      return "";
  }
}
